import { Guid } from 'guid-typescript';
import { HttpParams } from '@angular/common/http';
import { BudgetPlan } from './submission';
import { ApplicationStatus } from '../enums/application-status';
import { ApplicationAction, ApplicationStateMachine } from '../states/application.machine';


export interface CreateApplication {
  associationId: string;
  grantId: Guid;
  category?: ApplicationCategory;
  area?: ApplicationArea;
  name?: string;
  year: number;
  requestedGrantAmount: number;
  organizationName?: string;
  address?: string;
  postalCodeAndHeadquarters?: string;
  county?: string;
  authorizedRepresentative?: string;
  authorizedRepresentativeEmail?: string;
  authorizedRepresentativeRole?: string;
  telephone?: string;
  mobilePhone?: string;
  fax?: string;
  organizationEmail?: string;
  website?: string;
  yearOfEstablishment: number;
  dateOfRegistration: Date;
  registrationNumber?: string;
  registeredAuthority?: string;
  bankName?: string;
  bankAccountNumber?: string;
  oib?: string;
  rno?: string;
  objectivesAccordingToStatute?: string;
  purposeAndFieldOfOperation?: string;
  activitiesAccordingToStatute?: string;
  totalMembers: number;
  totalIndividualMembers: number;
  totalLegalEntities: number;
  volunteerWorkShare: number;
  numberOfVolunteers: number;
  volunteerHoursInPreviousYear: number;
  temporaryEmployees: number;
  permanentEmployees: number;
  isInVATSystem: boolean;
  totalIncomeInPreviousYear: number;
  incomeFromGovernmentDonationsInPreviousYear: number;
  incomeFromLocalAndRegionalAuthoritiesInPreviousYear: number;
  incomeFromInternationalOrganizationsInPreviousYear: number;
  incomeFromBusinessesAndLegalEntitiesInPreviousYear: number;
  incomeFromIndividualsInPreviousYear: number;
  incomeFromNonprofitOrganizationsInPreviousYear: number;
  membershipFeesInPreviousYear: number;
  euFundsInPreviousYear: number;
  totalSalaryPaidInPreviousYear: number;
  totalCompensationPaidInPreviousYear: number;
  ownSpaceSize: number;
  rentedSpaceSize: number;
  municipalSpaceSize: number;
  monthlyRentForMunicipalSpace: number;
  totalApprovedGrantsInPreviousYear: number;
  numberOfPartnerships: number;
  isAnnualReportPrepared: boolean;
  reportRecipients?: string;
  implementsQualitySystem: boolean;
  qualitySystemDetails?: string;
  programProjectName?: string;
  summary?: string;
  expectedDurationMonths?: number;
  geographicalArea?: string;
  totalAmountRequired?: number;
  amountRequestedFromMunicipality?: number;
  additionalFundingSources?: string;
  motivationDescription?: string;
  projectGoals?: string;
  expectedResults?: string;
  contributionToGoals?: string;
  targetGroups?: string;
  endUsersImpact?: string;
  projectLeader?: string;
  numberOfVolunteersInvolved?: number;
  numberOfEmployeesInvolved?: number;
  similarExperience?: string;
  publicEngagementPlan?: string;
  fileStorageLocation?: string;
  activities: ApplicationActivity[],
  budgetPlan: BudgetPlan
}

export interface UpdateApplication {
  id: Guid;
  category?: ApplicationCategory;
  area?: ApplicationArea;
  name?: string;
  year: number;
  requestedGrantAmount: number;
  organizationName?: string;
  address?: string;
  postalCodeAndHeadquarters?: string;
  county?: string;
  authorizedRepresentative?: string;
  authorizedRepresentativeEmail?: string;
  authorizedRepresentativeRole?: string;
  telephone?: string;
  mobilePhone?: string;
  fax?: string;
  organizationEmail?: string;
  website?: string;
  yearOfEstablishment: number;
  dateOfRegistration: Date;
  registrationNumber?: string;
  registeredAuthority?: string;
  bankName?: string;
  bankAccountNumber?: string;
  oib?: string;
  rno?: string;
  objectivesAccordingToStatute?: string;
  purposeAndFieldOfOperation?: string;
  activitiesAccordingToStatute?: string;
  totalMembers: number;
  totalIndividualMembers: number;
  totalLegalEntities: number;
  volunteerWorkShare: number;
  numberOfVolunteers: number;
  volunteerHoursInPreviousYear: number;
  temporaryEmployees: number;
  permanentEmployees: number;
  isInVATSystem: boolean;
  totalIncomeInPreviousYear: number;
  incomeFromGovernmentDonationsInPreviousYear: number;
  incomeFromLocalAndRegionalAuthoritiesInPreviousYear: number;
  incomeFromInternationalOrganizationsInPreviousYear: number;
  incomeFromBusinessesAndLegalEntitiesInPreviousYear: number;
  incomeFromIndividualsInPreviousYear: number;
  incomeFromNonprofitOrganizationsInPreviousYear: number;
  membershipFeesInPreviousYear: number;
  euFundsInPreviousYear: number;
  totalSalaryPaidInPreviousYear: number;
  totalCompensationPaidInPreviousYear: number;
  ownSpaceSize: number;
  rentedSpaceSize: number;
  municipalSpaceSize: number;
  monthlyRentForMunicipalSpace: number;
  totalApprovedGrantsInPreviousYear: number;
  numberOfPartnerships: number;
  isAnnualReportPrepared: boolean;
  reportRecipients?: string;
  implementsQualitySystem: boolean;
  qualitySystemDetails?: string;
  programProjectName?: string;
  summary?: string;
  expectedDurationMonths?: number;
  geographicalArea?: string;
  totalAmountRequired?: number;
  amountRequestedFromMunicipality?: number;
  additionalFundingSources?: string;
  motivationDescription?: string;
  projectGoals?: string;
  expectedResults?: string;
  contributionToGoals?: string;
  targetGroups?: string;
  endUsersImpact?: string;
  projectLeader?: string;
  numberOfVolunteersInvolved?: number;
  numberOfEmployeesInvolved?: number;
  similarExperience?: string;
  publicEngagementPlan?: string;
  fileStorageLocation?: string;
  activities: ApplicationActivity[],
  budgetPlan: BudgetPlan;
}

export enum ApplicationCategory {
  Program = 1,
  Project = 2,
  Manifestation = 3
}

export enum ApplicationArea {
  SportAndRecreation = 1,
  Culture = 2,
  OtherSocialActivities = 3
}

export interface SubmitApplicationCompliance {
  applicationId: string;
  evaluatorId: string;
  isDeliveredToCorrectCall: boolean | null;
  isDeliveredWithinDeadline: boolean | null;
  isApplicationInCroatianLanguage: boolean | null;
  areAllMandatoryFieldsFilled: boolean | null;
  areApplicantAndPartnerEligible: boolean | null;
  isImplementationLocationAcceptable: boolean | null;
  isRequestedAmountWithinLimits: boolean | null;
  isNoDoubleFundingStatementSubmitted: boolean | null;
  isNoDebtCertificateSubmitted: boolean | null;
  isNoCriminalProceedingsCertificateSubmitted: boolean | null;
  isCriminalRecordCheckConsentSubmitted: boolean | null;
  isStatuteProofSubmitted: boolean | null;
  isPartnershipStatementSubmitted: boolean | null;
  isAssociationRegistryProofSubmitted: boolean | null;
  isNonProfitRegistryProofSubmitted: boolean | null;
  isNoOutstandingObligationsStatementSubmitted: boolean | null;
}


export interface SubmitApplicationReview {
  applicationId: Guid;
  evaluatorId: Guid;
  strategicGoalAchievementScore: number;
  activityClarityAndFeasibilityScore: number;
  localNeedsAlignmentScore: number;
  communityLifeEnhancementScore: number;
  participantDefinitionScore: number;
  managementAndExpertiseScore: number;
  costRealismScore: number;
  budgetItemLinkageScore: number;
  communityInvolvementScore: number;
  publicCommunicationAndPromotionScore: number;
  comments: string;
}

export interface Application {
  id: Guid;
  associationId: number;
  grantId: Guid;
  name?: string;
  year: number;
  requestedGrantAmount: number;
  organizationName?: string;
  address?: string;
  postalCodeAndHeadquarters?: string;
  county?: string;
  authorizedRepresentative?: string;
  authorizedRepresentativeEmail?: string;
  authorizedRepresentativeRole?: string;
  telephone?: string;
  mobilePhone?: string;
  fax?: string;
  organizationEmail?: string;
  website?: string;
  yearOfEstablishment: number;
  dateOfRegistration: Date;
  registrationNumber?: string;
  registeredAuthority?: string;
  bankName?: string;
  bankAccountNumber?: string;
  oib?: string;
  rno?: string;
  objectivesAccordingToStatute?: string;
  purposeAndFieldOfOperation?: string;
  activitiesAccordingToStatute?: string;
  totalMembers: number;
  totalIndividualMembers: number;
  totalLegalEntities: number;
  volunteerWorkShare: number;
  numberOfVolunteers: number;
  volunteerHoursInPreviousYear: number;
  temporaryEmployees: number;
  permanentEmployees: number;
  isInVATSystem: boolean;
  totalIncomeInPreviousYear: number;
  incomeFromGovernmentDonationsInPreviousYear: number;
  incomeFromLocalAndRegionalAuthoritiesInPreviousYear: number;
  incomeFromInternationalOrganizationsInPreviousYear: number;
  incomeFromBusinessesAndLegalEntitiesInPreviousYear: number;
  incomeFromIndividualsInPreviousYear: number;
  incomeFromNonprofitOrganizationsInPreviousYear: number;
  membershipFeesInPreviousYear: number;
  euFundsInPreviousYear: number;
  totalSalaryPaidInPreviousYear: number;
  totalCompensationPaidInPreviousYear: number;
  ownSpaceSize: number;
  rentedSpaceSize: number;
  municipalSpaceSize: number;
  monthlyRentForMunicipalSpace: number;
  totalApprovedGrantsInPreviousYear: number;
  numberOfPartnerships: number;
  isAnnualReportPrepared: boolean;
  reportRecipients?: string;
  implementsQualitySystem: boolean;
  qualitySystemDetails?: string;
  programProjectName?: string;
  summary?: string;
  expectedDurationMonths?: number;
  geographicalArea?: string;
  totalAmountRequired?: number;
  amountRequestedFromMunicipality?: number;
  additionalFundingSources?: string;
  motivationDescription?: string;
  projectGoals?: string;
  expectedResults?: string;
  contributionToGoals?: string;
  targetGroups?: string;
  endUsersImpact?: string;
  projectLeader?: string;
  numberOfVolunteersInvolved?: number;
  numberOfEmployeesInvolved?: number;
  similarExperience?: string;
  publicEngagementPlan?: string;
  fileStorageLocation?: string;
  activities: ApplicationActivity[],
  budgetPlan: BudgetPlan;
}


export interface ApplicationCompliance {
  evaluatorUsername: string | null;
  evaluationDate: Date;

  isDeliveredToCorrectCall: boolean | null;
  isDeliveredWithinDeadline: boolean | null;
  isApplicationInCroatianLanguage: boolean | null;
  areAllMandatoryFieldsFilled: boolean | null;
  areApplicantAndPartnerEligible: boolean | null;
  isImplementationLocationAcceptable: boolean | null;
  isRequestedAmountWithinLimits: boolean | null;
  isNoDoubleFundingStatementSubmitted: boolean | null;
  isNoDebtCertificateSubmitted: boolean | null;
  isNoCriminalProceedingsCertificateSubmitted: boolean | null;
  isCriminalRecordCheckConsentSubmitted: boolean | null;
  isStatuteProofSubmitted: boolean | null;
  isPartnershipStatementSubmitted: boolean | null;
  isAssociationRegistryProofSubmitted: boolean | null;
  isNonProfitRegistryProofSubmitted: boolean | null;
  isNoOutstandingObligationsStatementSubmitted: boolean | null;
}

export interface ApplicationReview {
  evaluatorUsername: string | null;
  reviewDate: Date;

  strategicGoalAchievementScore: number;
  activityClarityAndFeasibilityScore: number;
  localNeedsAlignmentScore: number;
  communityLifeEnhancementScore: number;
  participantDefinitionScore: number;
  managementAndExpertiseScore: number;
  costRealismScore: number;
  budgetItemLinkageScore: number;
  communityInvolvementScore: number;
  publicCommunicationAndPromotionScore: number;

  totalScore: number;

  comments: string;
}

export interface ApplicationActivity{
  id: Guid,
  name: string,
  description: string,
  startDate: Date,
  endDate: Date,
  results: string
}

export class ApplicationSummary {
  id: Guid;
  associationId: number;
  grantName: string;
  organizationName: string;
  programProjectName: string;
  status: ApplicationStatus;
  isProjectCreated: boolean;
  submittedAt: Date;


  constructor(
    id: Guid,
    associationId: number,
    grantName: string,
    organizationName: string,
    programProjectName: string,
    status: ApplicationStatus,
    isProjectCreated: boolean,
    submittedAt: Date
  ) {
    this.id = id;
    this.associationId = associationId;
    this.grantName = grantName;
    this.organizationName = organizationName;
    this.programProjectName = programProjectName;
    this.status = status;
    this.isProjectCreated = isProjectCreated;
    this.submittedAt = submittedAt;
  }

    isActionAllowed(action: ApplicationAction): boolean {
      return ApplicationStateMachine.can(action, this.status);
    }

  get userFriendlyStatus(): string {
    switch (this.status) {
      case ApplicationStatus.Draft:
        return 'Skica';
      case ApplicationStatus.Submitted:
        return 'Prijava predana';
      case ApplicationStatus.Returned:
        return 'Prijava vraćena na doradu';
      case ApplicationStatus.Resubmitted:
        return 'Prijava ponovno predana';
      case ApplicationStatus.InCompliance:
          return 'Prijava u obradi';
      case ApplicationStatus.NonCompliance:
          return 'Prijava u obradi';
      case ApplicationStatus.PositiveReview:
          return 'Prijava u obradi';
      case ApplicationStatus.NegativeReview:
            return 'Prijava u obradi';
      case ApplicationStatus.Approved:
        return 'Prijava odobrena';
      case ApplicationStatus.Rejected:
        return 'Prijava odbijena';
      default:
        return 'Nepoznat status';
    }
  }

  get associationFriendlyStatus(): string {
    return this.userFriendlyStatus;
  }

  // Municipality-friendly status with a more detailed mapping for all statuses.
  get municipalityFriendlyStatus(): string {
    switch (this.status) {
      case ApplicationStatus.Draft:
        return 'Skica';
      case ApplicationStatus.Submitted:
        return 'Prijava predana';
      case ApplicationStatus.Returned:
        return 'Prijava vraćena na doradu';
      case ApplicationStatus.Resubmitted:
        return 'Prijava ponovno predana';
      case ApplicationStatus.InCompliance:
        return 'Ispunjeni formalni uvjeti';
      case ApplicationStatus.NonCompliance:
        return 'Formalni uvjeti nisu ispunjeni';
      case ApplicationStatus.PositiveReview:
        return 'Pozitivna ocjena';
      case ApplicationStatus.NegativeReview:
        return 'Negativna ocjena';
      case ApplicationStatus.Approved:
        return 'Prijava odobrena';
      case ApplicationStatus.Rejected:
        return 'Prijava odbijena';
      default:
        return 'Nepoznat status';
    }
  }

  static fromJson(data: any): ApplicationSummary {
    return new ApplicationSummary(
      Guid.parse(data.id),
      data.associationId,
      data.grantName,
      data.organizationName,
      data.programProjectName,
      data.status,
      data.isProjectCreated,
      data.submittedAt
    );
  }
}

export class GetApplicationsFilter {
  constructor(
      public municipalityId?: string | null,
      public associationId?: string | null,
      public status?: ApplicationStatus | null,
      public year?: number | null,
      public grantName?: string | null
  ) {}

  toQueryParams(): HttpParams {
      let params = new HttpParams();

      if (this.municipalityId) params = params.set('municipalityId', this.municipalityId);
      if (this.associationId) params = params.set('associationId', this.associationId);
      if (this.status) params = params.set('status', this.status);
      if (this.year) params = params.set('year', this.year);
      if (this.grantName) params = params.set('grantName', this.grantName);

      return params;
  }
}

export enum ApplicationFormType {
  B1 = 1,
  B2 = 2,
  B3 = 3,
  B6 = 4
}